import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HiOutlineHome, HiOutlineUsers, HiOutlineDocumentReport, HiOutlineBriefcase } from "react-icons/hi";
import { FaDollarSign, FaUserTie } from "react-icons/fa";
import logo from "../../../Assets/Uni_Logo.png";
import { SidebarContext } from "../SidebarContext";
import { UserContext } from "../../../Context/User";
import { EmployeeContext } from "../../../Context/Employee";
import { SalesContext } from "../../../Context/Sales";
import { getValuesFromLocalstorage } from "../../Helpers";
import { BarChart3, LayoutDashboard, StickyNote, Users } from "lucide-react";
import LOCAL_KEYS, { parseLocalStorageJSON } from "../../../helpers/localstorage";
import { LoginContext } from "../../../Context/Auth";
import { toast } from "react-toastify";

const Sidebar = () => {
  const { isExpanded, isMobile, isSidebarVisible, toggleSidebar } = useContext(SidebarContext);
  const { totalUsers, currentUserDetails } = useContext(UserContext);
  const { totalEmployees } = useContext(EmployeeContext);
  const { totalSales } = useContext(SalesContext);
  const profileDetails = parseLocalStorageJSON(LOCAL_KEYS.PROFILE_DETAILS);
  const {getProfilePhoto} = useContext(LoginContext);
  const profileAvatar = getProfilePhoto();
  const location = useLocation();
  const navigate = useNavigate();
  const [openSubMenu, setOpenSubMenu] = useState(null);
  
  // Check for password expiry
  const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED) || false;
  
  // Get user details to check account type
  const isMarketingExec = currentUserDetails?.acctType === "MKTG_EXEC";
  const isRecruiter = currentUserDetails?.acctType === "RECRUITER";
  const isAdmin = currentUserDetails?.acctType === "ADMIN";
  const isRecruiterOrAdmin = isRecruiter || isAdmin;
  // console.log("Sidebar - currentUserDetails:", currentUserDetails);
  // console.log("Sidebar - isMarketingExec:", isMarketingExec);
  // console.log("Sidebar - isRecruiterOrAdmin:", isRecruiterOrAdmin);
  const username = currentUserDetails?.username || currentUserDetails?.name || currentUserDetails?.firstName || "User";
  
  // Handle navigation click with password expiry check
  const handleNavClick = (e, item) => {
    if (isPasswordExpired && item.path !== "/updatePassword") {
      e.preventDefault();
      toast.error("Please change your password. Your password has expired.");
      navigate("/updatePassword");
      return false;
    }
    
    if (item.hasChildren) {
      e.preventDefault();
      setOpenSubMenu(openSubMenu === item.name ? null : item.name);
      // Navigate to the parent route
      navigate(item.path);
      return false;
    }
    
    if (location.pathname === item.path) {
      window.location.reload();
    }
    return true;
  };

  // Handle submenu item click
  const handleSubMenuClick = (e, subItem) => {
    e.preventDefault(); // Prevent default link behavior
    
    if (isPasswordExpired && subItem.path !== "/updatePassword") {
      toast.error("Please change your password. Your password has expired.");
      navigate("/updatePassword");
      return false;
    }
    
    navigate(subItem.path);
    // Keep the submenu open (don't change openSubMenu state)
    // Only close sidebar on mobile
    if (isMobile) {
      toggleSidebar();
    }
    return true;
  };
  
  // Define all menu items with correct order
  const allMenuItems = [
    { name: "Dashboard", path: "/", icon: <LayoutDashboard className="w-5 h-5" /> },
    {
      name: "Notes",
      path: "/notes",
      icon: <StickyNote className="w-5 h-5" />,
    },
    { 
      name: "Users", 
      path: "/users", 
      icon: <Users className="w-5 h-5" />,
      count: totalUsers,
      adminOnly: true 
    },
    { 
      name: "Sales", 
      path: "/sales", 
      icon: <FaDollarSign className="w-5 h-5" />,
      count: totalSales || 0,
      recruiterExcluded: true
    },
    { 
      name: "Consultants", 
      path: "/manage-employees", 
      icon: <FaUserTie className="w-5 h-5" />,
      count: totalEmployees,
      recruiterExcluded: true
    },
    // Remove Reports parent, add Submissions Table as main item
    {
      name: "Reports",
      path: "/reports/submissions",
      icon: <BarChart3 className="w-5 h-5" />,
      recruiterExcluded: true
    },
    {
      name: "Candidates",
      path: "/candidates",
      icon: <HiOutlineUsers className="w-[18px] h-[18px] shrink-0" />,
      recruiterOnly: true,
      showSeparatorBefore: true
    },
    {
      name: "Requirements",
      path: "/requirements",
      icon: <HiOutlineBriefcase className="w-[18px] h-[18px] shrink-0" />,
      recruiterOnly: true
    },
   
  ];
  
  // Filter items based on user role
  const menuItems = allMenuItems.filter(item => {
    // Admin: show all items (admin-only and recruiter-only)
    if (isAdmin) {
      return true;
    }
    // Recruiter: show only Dashboard and Requirements (exclude admin-only, marketing-exec-only, and recruiter-excluded items)
    if (isRecruiter) {
      return !item.adminOnly && !item.marketingExecOnly && !item.recruiterExcluded;
    }
    // Marketing executives: exclude admin-only and recruiter-only items
    if (isMarketingExec) {
      return !item.adminOnly && !item.recruiterOnly;
    }
    // Other users: exclude admin-only and recruiter-only items
    return !item.adminOnly && !item.recruiterOnly;
  });



  // Auto-open submenu if we're on a child page
  useEffect(() => {
    const currentPath = location.pathname;
    const reportsItem = menuItems.find(item => item.name === "Reports");
    if (reportsItem?.hasChildren) {
      const isOnChildPage = reportsItem.children.some(child => child.path === currentPath);
      if (isOnChildPage) {
        setOpenSubMenu("Reports");
      }
    }
  }, [location.pathname, menuItems]);

  // If mobile and sidebar is not visible, don't render
  if (isMobile && !isSidebarVisible) {
    return null;
  }

  return (
    <>
      {isMobile && isSidebarVisible && (
        <div className="sidebar-overlay" onClick={toggleSidebar} />
      )}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: isExpanded ? '200px' : '70px',
        backgroundColor:'#F5F5F5',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        zIndex: isMobile ? 50 : 30,
        transition: 'all 0.3s ease',
        overflowY: 'auto',
        overflowX: 'hidden',
        borderRight: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Segoe UI, system-ui, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
        transform: isMobile && !isSidebarVisible ? 'translateX(-100%)' : 'translateX(0)',
      }}>
        <div className="flex items-center px-6 py-6">
          <img src={logo} alt="Logo" className="h-8 w-7 object-contain" />
          {isExpanded && (
            <h2 className="text-xl font-bold text-slate-800 tracking-wide ml-3">UniMark</h2>
          )}
        </div>
        <nav className={`flex-1 px-4 pb-4 space-y-1 overflow-y-auto ${isExpanded ? 'px-4' : ''}`}>
          {menuItems.map((item, index) => (
            <div key={item.name}>
              {item.showSeparatorBefore && isAdmin && (
                <div className="my-3">
                  {isExpanded ? (
                    <div className="flex items-center px-4 py-2">
                      <div className="flex-1 border-t border-gray-200"></div>
                      <span className="px-3 text-xs font-semibold text-[#201F1E] uppercase tracking-wider">Recruitment</span>
                      <div className="flex-1 border-t border-gray-200"></div>
                    </div>
                  ) : (
                    <div className="border-t border-gray-200"></div>
                  )}
                </div>
              )}
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `flex w-full min-w-0 rounded-[4px] border transition-colors duration-150 ${
                    isExpanded
                      ? "items-center gap-2 py-2 pl-2 pr-1.5 text-[13px] font-medium leading-tight"
                      : "justify-center items-center py-2.5 px-0 text-sm font-medium"
                  } ${
                    isActive || (item.hasChildren && item.children?.some(child => location.pathname === child.path))
                      ? "bg-white text-[#201F1E] border-[#D1D1D1]"
                      : "text-[#656464] border-transparent hover:bg-white hover:border-[#D1D1D1] hover:text-[#201F1E]"
                  }`
                }
                title={!isExpanded ? item.name : ''}
                end={!item.hasChildren}
                onClick={(e) => handleNavClick(e, item)}
              >
                <span className={isExpanded ? "mr-1" : ""}>{item.icon}</span>
                {isExpanded && (
                  <>
                    <span className="min-w-0 flex-1 truncate text-left" title={item.name}>
                      {item.name}
                    </span>
                    {typeof item.count === "number" && item.count > 0 && (
                      <span className="shrink-0 bg-[#EDEBE9] text-[#201F1E] min-w-[1.25rem] px-1 py-0.5 rounded text-[10px] font-semibold tabular-nums text-center leading-none">
                        {item.count}
                      </span>
                    )}
                    {item.hasChildren && (
                      <span className="ml-auto">
                        {openSubMenu === item.name ? "▼" : "►"}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
              
              {/* Submenu */}
              {item.hasChildren && openSubMenu === item.name && isExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  {item.children.map((subItem) => (
                    <NavLink
                      key={subItem.name}
                      to={subItem.path}
                      className={({ isActive }) =>
                        `flex items-center py-2 px-4 text-sm font-medium rounded-lg transition-colors ${
                          isActive
                            ? "bg-white text-[#201F1E] "
                            : "text-[#201F1E] hover:bg-gray-50"
                        }`
                      }
                      onClick={(e) => handleSubMenuClick(e, subItem)}
                    >
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                      {subItem.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        
        {/* User Profile Section */}
        <div className="mt-auto shrink-0 border-t border-[#D1D1D1] px-2 py-2.5">
          <div className={`flex items-center min-w-0 ${isExpanded ? "gap-2" : "justify-center"}`}>
            <div className="relative shrink-0">
              <div
                className={`w-9 h-9 rounded-full border border-[#D1D1D1] flex items-center justify-center font-semibold text-xs ${profileAvatar.bgColorClass} ${profileAvatar.textColorClass}`}
              >
                {profileAvatar.initials}
              </div>
            </div>
            
            {isExpanded && (
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="text-[13px] font-semibold text-[#201F1E] truncate leading-tight">
                  {currentUserDetails?.firstName && currentUserDetails?.lastName 
                    ? `${currentUserDetails.firstName} ${currentUserDetails.lastName}` 
                    : username}
                </p>
                <p className="text-[11px] font-normal text-[#605E5C] truncate leading-tight mt-0.5">
                  {profileDetails?.role || profileDetails?.acctType || "User"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;